import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Alert, Grid, Modal, Skeleton, Typography, message } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  type BlockerFunction,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import usePrompt from "../../../../hooks/use-prompt.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name.ts"
import { getReportDetails, updateReport } from "../../api/services.ts"
import type { CreateReportData } from "../../api/services.types.ts"
import { normalizeDefinition } from "../../helpers/reportDefinition.ts"
import {
  DEFAULT_REPORT_ICON,
  reportIconKeyOf
} from "../../helpers/reportIcons.tsx"
import {
  buildReportSettingsPatch,
  type ReportSettingsPatch
} from "../../helpers/reportSettings.ts"
import type { ShareEditorEntry } from "../../helpers/reportSharing.ts"
import {
  listHrefFromParams,
  presetFolderFromSearchParams
} from "../../helpers/reportsNav.ts"
import { useReportCanvasAutosave } from "../../hooks/useReportCanvasAutosave.ts"
import { useReportFolderNames } from "../../hooks/useReportFolderNames.ts"
import {
  reportCanvasHydrate,
  reportCanvasReset,
  reportCanvasSetConfigWidgetId
} from "../../redux/reportCanvas.slice.ts"
import { fetchReportCatalog } from "../../redux/reportCanvas.thunks.ts"
import {
  createReport,
  deleteReport,
  fetchReports,
  toggleReportFavorite
} from "../../redux/reports.thunks.ts"
import type { ReportDefinitionV1 } from "../../types/canvas.types.ts"
import type { ReportDetail, ReportVisibility } from "../../types/types.ts"
import { ReportBuildLayout } from "../build/ReportBuildLayout.tsx"
import { ReportFiltersStep } from "../filters/ReportFiltersStep.tsx"
import {
  ReportWizardFooter,
  ReportWizardStepper,
  type WizardStep
} from "./ReportWizardHeader.tsx"
import {
  ReportWizardStepGeneral,
  type WizardGeneralInfo
} from "./ReportWizardStepGeneral.tsx"
import { ReportWizardStepReview } from "./ReportWizardStepReview.tsx"
import type { ReportWizardShareHandle } from "./ReportWizardStepShare.tsx"
import { ReportWizardSuccess } from "./ReportWizardSuccess.tsx"

const { Title, Text } = Typography

const emptyDefinition = (icon: string): ReportDefinitionV1 => ({
  version: 1,
  widgets: [],
  layout: [],
  filters: {},
  icon
})

const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  1: "reports.wizard.steps.general",
  2: "reports.wizard.steps.build",
  3: "reports.wizard.steps.filters",
  4: "reports.wizard.steps.share"
}

const INITIAL_GENERAL: WizardGeneralInfo = {
  name: "",
  description: "",
  tags: [],
  folder: "",
  favorite: false,
  visibility: "private",
  icon: DEFAULT_REPORT_ICON
}

// Anything typed before the draft exists would be lost by leaving, so leaving
// asks first; an untouched form (a preselected folder alone) does not.
const hasEnteredDetails = (general: WizardGeneralInfo, preset: string) =>
  Boolean(
    general.name.trim() ||
    general.description.trim() ||
    general.tags.length ||
    general.folder !== preset ||
    general.favorite ||
    general.icon !== DEFAULT_REPORT_ICON
  )

const ReportWizardPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { id: resumeId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  // The list the modal was opened over; leaving returns to exactly that view.
  const listHref = listHrefFromParams(searchParams)
  const screens = Grid.useBreakpoint()
  const catalogStatus = useAppSelector((s) => s.reportCanvas.catalogStatus)
  const reports = useAppSelector((s) => s.reports.results)
  const userInfo = useAppSelector((s) => s.auth.userInfo)
  const folderOptions = useReportFolderNames()

  const [presetFolder] = useState(() =>
    resumeId ? "" : presetFolderFromSearchParams(searchParams)
  )
  const [step, setStep] = useState<WizardStep>(1)
  const [completed, setCompleted] = useState<Set<WizardStep>>(new Set())
  const [general, setGeneral] = useState<WizardGeneralInfo>(() => ({
    ...INITIAL_GENERAL,
    folder: presetFolder
  }))
  const [nameError, setNameError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [stepError, setStepError] = useState<string | null>(null)
  const [created, setCreated] = useState<ReportDetail | null>(null)
  const [done, setDone] = useState(false)
  const [shareEntries, setShareEntries] = useState<ShareEditorEntry[] | null>(
    null
  )
  const shareRef = useRef<ReportWizardShareHandle>(null)
  const finishing = useRef(false)
  // Set synchronously, so a second click lands before React re-renders `busy`.
  const working = useRef(false)
  const resuming = Boolean(resumeId) && !created

  const { saveNow, cancel: cancelAutosave } = useReportCanvasAutosave(
    created?.id,
    false
  )

  const dirtyBeforeDraft = !created && hasEnteredDetails(general, presetFolder)

  // Once Continue has POSTed the draft, *any* exit that isn't Create Report
  // strands it — the Cancel button, the sidebar, browser Back and a reload
  // alike. Before that, only what was typed is at stake.
  const blockExit = useCallback<BlockerFunction>(
    (args) => {
      if (finishing.current) return false
      if (!created && !dirtyBeforeDraft) return false
      const next = args?.nextLocation
      if (!next) return true
      return !next.pathname.startsWith(RoutesPaths.reports.create)
    },
    [created, dirtyBeforeDraft]
  )

  const blocker = usePrompt(blockExit)
  const discardOpen = blocker.state === "blocked"

  useEffect(() => {
    generateDocumentTitle(t("reports.wizard.title"))
  }, [t])

  useEffect(() => {
    if (catalogStatus === "idle") void dispatch(fetchReportCatalog())
  }, [dispatch, catalogStatus])

  useEffect(() => {
    if (reports.length === 0) void dispatch(fetchReports({}))
  }, [dispatch, reports.length])

  useEffect(() => () => void dispatch(reportCanvasReset()), [dispatch])

  // A refresh at step 2+ lands back here with the created report id in the
  // route; re-fetch it and resume at Build instead of orphaning the report and
  // POSTing a duplicate on the next advance.
  //
  // Deliberately NOT depending on dispatch/navigate/t: this effect's only
  // reactive trigger is "which report are we resuming, and have we already
  // got it" — dispatch and navigate are stable by their own contracts, and t
  // is read only for a static translation key inside the async callback, so
  // neither needs to be fresh. Including them would let a dependency that is
  // merely *supposed* to be stable (as this component's own tests prove by
  // mocking useTranslation with a fresh `t` every render) re-enter this
  // effect — which starts a new fetch while the previous one is still
  // in-flight, and React tearing down that previous effect's cleanup (which
  // every re-run does, regardless of why) cancels it before it can commit,
  // so the resume loops forever without ever setting `created`.
  useEffect(() => {
    if (!resumeId || created) return
    let cancelled = false
    void (async () => {
      const response = await getReportDetails(resumeId)
      if (cancelled) return
      if (!response.ok || !response.data) {
        if (response.status === 404)
          message.error(t("reports.wizard.draftExpired"))
        navigate(listHref, { replace: true })
        return
      }
      const detail = response.data as ReportDetail
      if (!detail.is_draft) {
        navigate(RoutesPaths.reports.view(detail.id), { replace: true })
        return
      }
      // An empty PATCH still reaches the model's save(), which bumps `updated`
      // and so keeps the draft out of the cleanup task's reach.
      void updateReport(resumeId, {})
      const definition = normalizeDefinition(detail.definition)
      setCreated(detail)
      setGeneral({
        name: detail.name,
        description: detail.description ?? "",
        tags: detail.tags ?? [],
        folder: detail.folder ?? "",
        favorite: detail.is_favorite,
        visibility: detail.visibility,
        icon: reportIconKeyOf(definition)
      })
      dispatch(reportCanvasHydrate({ reportId: detail.id, definition }))
      setCompleted(new Set<WizardStep>([1]))
      setStep(2)
    })()
    return () => {
      cancelled = true
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the only trigger is which report is resumed; an unstable t in this list once caused a request loop
  }, [resumeId, created])

  const markCompleted = (n: WizardStep) =>
    setCompleted((prev) => new Set(prev).add(n))

  const handleGeneralChange = (patch: Partial<WizardGeneralInfo>) => {
    if (
      patch.visibility !== undefined &&
      patch.visibility !== general.visibility &&
      shareEntries !== null
    ) {
      // Emptied, not discarded: the Share step re-fetches while its list is
      // absent, and would restore what this just cleared.
      setShareEntries([])
    }
    setGeneral((prev) => ({ ...prev, ...patch }))
    if (patch.name !== undefined && patch.name.trim()) setNameError(false)
  }

  const createFromGeneral = async (): Promise<boolean> => {
    if (!general.name.trim()) {
      setNameError(true)
      return false
    }
    const payload: CreateReportData = {
      name: general.name.trim(),
      description: general.description.trim() || undefined,
      tags: general.tags,
      visibility: general.visibility,
      favorite: general.favorite,
      folder: general.folder || undefined,
      definition: emptyDefinition(general.icon),
      is_draft: true
    }
    const action = await dispatch(createReport(payload))
    if (!createReport.fulfilled.match(action)) return false
    const detail = action.payload?.details?.data as ReportDetail | undefined
    if (!detail) return false
    setCreated(detail)
    dispatch(
      reportCanvasHydrate({
        reportId: detail.id,
        definition: normalizeDefinition(detail.definition)
      })
    )
    // Encode the created id in the URL so a refresh resumes this report.
    navigate(
      `${RoutesPaths.reports.createResume(detail.id)}${
        searchParams.toString() ? `?${searchParams}` : ""
      }`,
      { replace: true }
    )
    return true
  }

  const persistGeneralEdits = async (): Promise<boolean> => {
    if (!created) return true
    if (!general.name.trim()) {
      setNameError(true)
      return false
    }
    const patch: ReportSettingsPatch = buildReportSettingsPatch(created, {
      name: general.name,
      description: general.description,
      folder: general.folder,
      tags: general.tags
    })

    if (Object.keys(patch).length > 0) {
      const response = await updateReport(created.id, patch)
      if (!response.ok) {
        setStepError(t("reports.wizard.detailsSaveError"))
        return false
      }
      setCreated((prev) => (prev ? { ...prev, ...patch } : prev))
    }
    if (general.favorite !== created.is_favorite) {
      const action = await dispatch(
        toggleReportFavorite({
          id: created.id,
          previousIsFavorite: created.is_favorite
        })
      )
      if (toggleReportFavorite.fulfilled.match(action))
        setCreated((prev) =>
          prev ? { ...prev, is_favorite: general.favorite } : prev
        )
    }
    return true
  }

  // Leaving step 1 for a later one saves what it holds; the other steps edit
  // the canvas, which saves itself.
  const leaveStep = async (): Promise<boolean> => {
    if (step !== 1) return true
    return created ? persistGeneralEdits() : createFromGeneral()
  }

  const run = async (work: () => Promise<void>) => {
    if (working.current) return
    working.current = true
    setBusy(true)
    setStepError(null)
    try {
      await work()
    } finally {
      working.current = false
      setBusy(false)
    }
  }

  const finish = async () => {
    if (!created) return
    const { ok: canvasSaved } = await saveNow()
    if (!canvasSaved) {
      setStepError(t("reports.wizard.finishSaveError"))
      return
    }
    if (shareRef.current) {
      const ok = await shareRef.current.save()
      if (!ok) {
        setStepError(t("reports.wizard.shareSaveError"))
        return
      }
    }
    // Finalize last: any earlier failure must leave the report a draft, so the
    // discard guard stays correct instead of offering to delete a live report.
    const response = await updateReport(created.id, { is_draft: false })
    if (!response.ok) {
      setStepError(t("reports.wizard.finishError"))
      return
    }
    finishing.current = true
    setDone(true)
    // The list behind the modal gains the new report.
    void dispatch(fetchReports({}))
  }

  const handleNext = () =>
    run(async () => {
      if (resuming) return
      if (step === 4) return finish()
      if (!(await leaveStep())) return
      if (step === 2) dispatch(reportCanvasSetConfigWidgetId(null))
      markCompleted(step)
      setStep((step + 1) as WizardStep)
    })

  const handleBack = () => {
    setStepError(null)
    setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s))
  }

  const canGoTo = (target: WizardStep) =>
    Boolean(created) &&
    !busy &&
    (target < step || target === 1 || completed.has((target - 1) as WizardStep))

  const handleGoTo = (target: WizardStep) =>
    run(async () => {
      if (target === step || !canGoTo(target)) return
      if (target > step && !(await leaveStep())) return
      if (step === 1) markCompleted(1)
      setStep(target)
    })

  const leave = () => navigate(listHref)

  const discardAndLeave = async () => {
    if (!created) {
      finishing.current = true
      blocker.proceed?.()
      return
    }
    setBusy(true)
    try {
      cancelAutosave()
      const action = await dispatch(deleteReport({ id: created.id }))
      if (!deleteReport.fulfilled.match(action)) {
        blocker.reset?.()
        return
      }
      finishing.current = true
      blocker.proceed?.()
    } finally {
      setBusy(false)
    }
  }

  const nextLabel =
    step === 1
      ? t("reports.wizard.nextBuild")
      : step === 2
        ? t("reports.wizard.nextFilters")
        : step === 3
          ? t("reports.wizard.nextShare")
          : t("reports.wizard.finish")

  // A step change unmounts the button that caused it, which would drop focus
  // onto <body>, outside the dialog: Escape and Tab would stop working there.
  // Focus moves to the new step's content instead.
  const stepBodyRef = useRef<HTMLDivElement>(null)
  const firstStepRender = useRef(true)
  useEffect(() => {
    if (firstStepRender.current) {
      firstStepRender.current = false
      return
    }
    stepBodyRef.current?.focus({ preventScroll: true })
  }, [step, done])

  const fullScreen = screens.md === false
  const building = step === 2 && !done && Boolean(created)
  const width = fullScreen
    ? "100vw"
    : building
      ? "min(1400px, calc(100vw - 64px))"
      : "min(1080px, calc(100vw - 64px))"

  return (
    <>
      <Modal
        open
        centered={!fullScreen}
        // A click on the dimmed list must never throw the wizard away; the X,
        // Cancel and Escape all go through the leave guard instead.
        maskClosable={false}
        keyboard
        onCancel={leave}
        width={width}
        closable={{ "aria-label": t("reports.wizard.close") }}
        title={
          <div className="flex flex-col gap-4 pr-8">
            <div className="flex min-w-0 flex-col">
              <Title level={3} className="m-0 text-start">
                {t("reports.wizard.title")}
              </Title>
              <Text type="secondary" className="font-normal">
                {t("reports.wizard.subtitle")}
              </Text>
            </div>
            <ReportWizardStepper
              current={step}
              completed={completed}
              done={done}
              canGoTo={canGoTo}
              onGoTo={handleGoTo}
            />
          </div>
        }
        footer={
          done ? null : (
            <ReportWizardFooter
              current={step}
              nextLabel={nextLabel}
              busy={busy || resuming}
              onCancel={leave}
              onBack={handleBack}
              onNext={handleNext}
            />
          )
        }
        style={
          fullScreen
            ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }
            : { paddingBottom: 0 }
        }
        styles={{
          content: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            ...(fullScreen
              ? { height: "100dvh", borderRadius: 0 }
              : { maxHeight: "90vh", ...(building ? { height: "90vh" } : {}) })
          },
          header: {
            margin: 0,
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${neutral.border}`
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: 0
          },
          footer: { margin: 0, padding: 0 }
        }}
      >
        <div
          ref={stepBodyRef}
          tabIndex={-1}
          aria-label={done ? undefined : t(WIZARD_STEP_LABELS[step])}
          className={`min-h-0 flex-1 px-6 py-5 outline-none ${
            building ? "flex flex-col overflow-hidden" : "overflow-y-auto"
          }`}
          style={
            step === 1 || done ? undefined : { background: neutral.surface }
          }
        >
          {stepError && (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setStepError(null)}
              message={stepError}
              className="mb-4"
            />
          )}
          {done && created ? (
            <ReportWizardSuccess
              name={general.name.trim()}
              folder={general.folder}
              onOpen={() => navigate(RoutesPaths.reports.view(created.id))}
              onBackToReports={leave}
            />
          ) : (
            <>
              {resuming && <Skeleton active paragraph={{ rows: 6 }} />}
              {!resuming && step === 1 && (
                <ReportWizardStepGeneral
                  value={general}
                  onChange={handleGeneralChange}
                  folderOptions={folderOptions}
                  nameError={nameError}
                  ownerName={getEntityName(created ? created.owner : userInfo)}
                />
              )}
              {step === 2 && created && (
                <div className="min-h-0 flex-1">
                  <ReportBuildLayout
                    reportFilters="summary"
                    title={general.name}
                  />
                </div>
              )}
              {step === 3 && created && <ReportFiltersStep />}
              {step === 4 && created && (
                <ReportWizardStepReview
                  ref={shareRef}
                  reportId={created.id}
                  owner={created.owner}
                  general={general}
                  savedVisibility={created.visibility}
                  onVisibilityChange={(visibility: ReportVisibility) =>
                    handleGeneralChange({ visibility })
                  }
                  shareEntries={shareEntries}
                  onShareEntriesChange={setShareEntries}
                />
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        open={discardOpen}
        centered
        title={
          <span>
            <ExclamationCircleOutlined className="mr-2 text-amber-500" />
            {t("reports.wizard.discardConfirm.title")}
          </span>
        }
        okText={t("reports.wizard.discardConfirm.confirm")}
        okButtonProps={{ danger: true, loading: busy }}
        cancelText={t("reports.wizard.discardConfirm.keepEditing")}
        onOk={discardAndLeave}
        onCancel={() => blocker.reset?.()}
      >
        {created
          ? t("reports.wizard.discardConfirm.body", { name: created.name })
          : t("reports.wizard.discardConfirm.unsavedBody")}
      </Modal>
    </>
  )
}

export default ReportWizardPage
